import { useEffect, useMemo, useState } from "react";
import { api, inr, fmtDate, todayYmd } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SearchablePicker, { projectMatch, renderProjectRow } from "@/components/app/SearchablePicker";
import Pagination from "@/components/app/Pagination";
import { Plus, AlertTriangle, CalendarDays, Edit2, Search, Download, CheckCircle2, ListChecks, FileCheck2, PanelRightOpen } from "lucide-react";
import { toast } from "sonner";

const ymdLocal = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};
const amcDatePresets = () => {
  const now = new Date();
  const today = ymdLocal(now);
  const weekStart = new Date(now); weekStart.setDate(now.getDate() - now.getDay());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    today:      { from: today, to: today },
    week:       { from: ymdLocal(weekStart), to: today },
    month:      { from: ymdLocal(monthStart), to: ymdLocal(monthEnd) },
  };
};

export default function AMC() {
  const [tab, setTab] = useState("contracts");
  const [contracts, setContracts] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [datePreset, setDatePreset] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedContract, setSelectedContract] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const applyDatePreset = (key) => {
    setDatePreset(key);
    if (key === "all" || key === "custom") return;
    const r = amcDatePresets()[key];
    setDateFrom(r.from); setDateTo(r.to);
  };
  const clearDateFilter = () => { setDatePreset("all"); setDateFrom(""); setDateTo(""); };
  const dateActive = !!(dateFrom || dateTo);
  const inDateRange = (isoDate) => {
    if (!dateActive) return true;
    const d = (isoDate || "").slice(0, 10);
    if (!d) return false;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  };

  const load = () => Promise.all([
    api.get("/amc-contracts").then((r) => setContracts(r.data)),
    api.get("/amc/upcoming").then((r) => setUpcoming(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const totalRev = contracts.filter((c) => c.status === "Active").reduce((s, c) => s + (c.annual_fee_inr || 0), 0);

  const contractIndex = useMemo(() => {
    const m = {};
    contracts.forEach((c) => (m[c.id] = c));
    return m;
  }, [contracts]);

  // Flatten visits across contracts for the visit tabs
  const visits = useMemo(() => {
    const out = [];
    contracts.forEach((c) => {
      (c.visits || []).forEach((v) => {
        out.push({
          ...v,
          contract_id: c.id,
          customer_name: c.customer_name,
          phone: c.phone,
          plan: c.plan,
          place: c.place,
          location: c.location,
          consumer_number: c.consumer_number,
          project_id: c.project_id,
          contract_status: c.status,
        });
      });
    });
    return out;
  }, [contracts]);

  const matchesSearch = (obj, needle) => {
    if (!needle) return true;
    const s = needle.toLowerCase();
    return [obj.customer_name, obj.place, obj.location, obj.plan, obj.consumer_number, obj.phone, obj.notes, obj.technician_name]
      .some((v) => v && String(v).toLowerCase().includes(s));
  };

  const filteredContracts = useMemo(() => {
    return contracts.filter((c) => {
      if (statusFilter !== "all" && c.status !== statusFilter) return false;
      return matchesSearch(c, q);
    });
  }, [contracts, q, statusFilter]);

  const scheduledVisits = useMemo(() => {
    return visits
      .filter((v) => v.status === "scheduled" && matchesSearch(v, q) && inDateRange(v.scheduled_date))
      .sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
  }, [visits, q, dateFrom, dateTo]);

  const completedVisits = useMemo(() => {
    return visits
      .filter((v) => v.status === "completed" && matchesSearch(v, q) && inDateRange(v.completed_date || v.scheduled_date))
      .sort((a, b) => (b.completed_date || b.scheduled_date || "").localeCompare(a.completed_date || a.scheduled_date || ""));
  }, [visits, q, dateFrom, dateTo]);

  useEffect(() => { setPage(1); }, [tab, q, statusFilter, pageSize, dateFrom, dateTo]);

  const paged = useMemo(() => {
    const src = tab === "contracts" ? filteredContracts
      : tab === "scheduled" ? scheduledVisits
      : completedVisits;
    const totalPages = Math.max(1, Math.ceil(src.length / pageSize));
    const safe = Math.min(page, totalPages);
    return src.slice((safe - 1) * pageSize, safe * pageSize);
  }, [tab, filteredContracts, scheduledVisits, completedVisits, page, pageSize]);

  const currentTotal = tab === "contracts" ? filteredContracts.length
    : tab === "scheduled" ? scheduledVisits.length
    : completedVisits.length;

  const doExport = () => {
    const p = new URLSearchParams();
    p.set("fmt", "xlsx");
    p.set("kind", tab === "contracts" ? "contracts" : tab === "scheduled" ? "scheduled" : "completed");
    if (q.trim()) p.set("q", q.trim());
    if (tab === "contracts" && statusFilter !== "all") p.set("status", statusFilter);
    if (tab !== "contracts" && dateActive) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    }
    const a = document.createElement("a");
    a.href = `/api/amc/export?${p.toString()}`;
    a.click();
  };

  return (
    <div data-testid="amc-page">
      <PageHeader eyebrow="Phase 2" title="AMC Scheduler" subtitle="Annual Maintenance Contracts with auto-scheduled visits and renewal alerts." action={
        <div className="flex gap-2">
          <Button
            onClick={doExport}
            variant="outline"
            className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11"
            data-testid="amc-export-btn"
          >
            <Download className="w-4 h-4 mr-2" /> Download Excel
          </Button>
          <a href="/amc/calendar">
            <Button variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-11" data-testid="open-amc-calendar">
              Visit Calendar →
            </Button>
          </a>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480] uppercase tracking-widest text-xs font-bold h-11 px-6" data-testid="new-amc-button"><Plus className="w-4 h-4 mr-2" /> New AMC</Button>
            </DialogTrigger>
            {open && <AMCDialog onDone={() => { setOpen(false); load(); }} />}
          </Dialog>
        </div>
      } />
      <div className="p-4 sm:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard testid="amc-total" label="Active Contracts" value={contracts.filter((c) => c.status === "Active").length} accent="success" />
          <StatCard testid="amc-revenue" label="Annual Recurring Revenue" value={inr(totalRev)} />
          <StatCard testid="amc-expiring" label="Expiring within 60 days" value={upcoming.length} accent="warning" />
          <StatCard testid="amc-scheduled" label="Scheduled Visits" value={scheduledVisits.length} accent="primary" />
          <StatCard testid="amc-completed" label="Completed Visits" value={completedVisits.length} />
        </div>

        {upcoming.length > 0 && (
          <div className="flat-card p-5 border-l-4 border-l-[#F59E0B]">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#F59E0B]" />
              <div className="label-eyebrow">Renewal Reminders</div>
            </div>
            <div className="space-y-1">
              {upcoming.map((c) => (
                <div key={c.id} className="flex justify-between text-sm border-b border-[#F3F4F6] py-1.5 last:border-0">
                  <div><span className="font-semibold">{c.customer_name}</span> <span className="text-xs text-[#4B5563]">· {c.plan}</span></div>
                  <div className="text-xs font-bold text-[#F59E0B]">{c.days_to_expiry} days · expires {fmtDate(c.end_date)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-none border border-[#E5E7EB] bg-white p-0 flex-wrap h-auto">
            <TabsTrigger value="contracts" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="amc-tab-contracts">
              <ListChecks className="w-3.5 h-3.5" /> Contracts
            </TabsTrigger>
            <TabsTrigger value="scheduled" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="amc-tab-scheduled">
              <CalendarDays className="w-3.5 h-3.5" /> Scheduled Visits
            </TabsTrigger>
            <TabsTrigger value="completed" className="rounded-none data-[state=active]:bg-[#0a0a0a] data-[state=active]:text-white uppercase tracking-widest text-xs font-bold px-5 h-10 flex items-center gap-1.5" data-testid="amc-tab-completed">
              <FileCheck2 className="w-3.5 h-3.5" /> Completed Visits
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Search + status + date filter */}
        <div className="flat-card p-3 flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search className="w-4 h-4 text-[#9CA3AF]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search customer, place, plan, technician, notes…"
              className="rounded-none h-10 border-none focus-visible:ring-0"
              data-testid="amc-search"
            />
          </div>
          {tab === "contracts" && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="rounded-none w-40 h-10" data-testid="amc-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {["Active", "Paused", "Expired", "Cancelled"].map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {(tab === "scheduled" || tab === "completed") && (
            <>
              <Select value={datePreset} onValueChange={applyDatePreset}>
                <SelectTrigger className="rounded-none w-40 h-10" data-testid="amc-date-preset">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All dates</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="custom">Custom…</SelectItem>
                </SelectContent>
              </Select>
              {(datePreset === "custom" || datePreset === "all") && (
                <>
                  <Input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setDatePreset("custom"); }} className="rounded-none h-10 w-[140px]" data-testid="amc-date-from" />
                  <Input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setDatePreset("custom"); }} className="rounded-none h-10 w-[140px]" data-testid="amc-date-to" />
                </>
              )}
              {dateActive && (
                <Button onClick={clearDateFilter} variant="ghost" size="sm" className="rounded-none text-xs uppercase tracking-widest font-bold" data-testid="amc-date-clear">Clear Dates</Button>
              )}
            </>
          )}
          <div className="text-xs text-[#4B5563] ml-auto">
            Showing <b>{currentTotal}</b>
          </div>
        </div>

        {tab === "contracts" && (
          <ContractsTable
            rows={paged}
            fullLength={filteredContracts.length}
            totalContracts={contracts.length}
            onEdit={setEditing}
            onDetails={setSelectedContract}
          />
        )}

        {tab === "scheduled" && (
          <VisitsTable
            rows={paged}
            kind="scheduled"
            emptyMsg={visits.filter((v) => v.status === "scheduled").length === 0 ? "No scheduled visits yet." : "No visits match your search."}
            onChanged={load}
            contractIndex={contractIndex}
          />
        )}

        {tab === "completed" && (
          <VisitsTable
            rows={paged}
            kind="completed"
            emptyMsg={visits.filter((v) => v.status === "completed").length === 0 ? "No visits completed yet." : "No visits match your search."}
            onChanged={load}
            contractIndex={contractIndex}
          />
        )}

        <Pagination
          total={currentTotal}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testidPrefix="amc-pagination"
        />
      </div>

      <Sheet open={!!selectedContract} onOpenChange={(o) => !o && setSelectedContract(null)}>
        {selectedContract && (
          <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="amc-details-sheet">
            <ContractDetails c={selectedContract} onClose={() => setSelectedContract(null)} />
          </SheetContent>
        )}
      </Sheet>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        {editing && (
          <AMCDialog
            existing={editing}
            onDone={() => { setEditing(null); load(); }}
          />
        )}
      </Dialog>
    </div>
  );
}

function ContractsTable({ rows, fullLength, totalContracts, onEdit, onDetails }) {
  return (
    <div className="border border-[#E5E7EB] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <tr>{["Customer", "Place / Project", "Location", "Plan", "Fee/yr", "Visits", "Start", "End", "Status", ""].map((h) => <th key={h} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const visits = c.visits || [];
            const done = visits.filter((v) => v.status === "completed").length;
            const scheduled = visits.filter((v) => v.status === "scheduled").length;
            return (
              <tr key={c.id} className="border-b border-[#F3F4F6]" data-testid={`amc-row-${c.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{c.customer_name}</div>
                  {c.consumer_number && <div className="text-[10px] text-[#9CA3AF]">CN {c.consumer_number}</div>}
                </td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">{c.place || (c.project_id ? "Linked project" : <span className="text-[#9CA3AF]">—</span>)}</td>
                <td className="px-4 py-3 text-xs text-[#4B5563]">
                  {c.location
                    ? (c.location.startsWith("http")
                        ? <a href={c.location} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline">Open map ↗</a>
                        : <span className="line-clamp-2">{c.location}</span>)
                    : <span className="text-[#9CA3AF]">—</span>}
                </td>
                <td className="px-4 py-3 text-xs">{c.plan}</td>
                <td className="px-4 py-3">{inr(c.annual_fee_inr)}</td>
                <td className="px-4 py-3 text-xs">
                  <div>{c.visits_per_year}/yr · <b>{visits.length}</b> total</div>
                  <div className="text-[10px] text-[#4B5563]">
                    <span className="text-[#10B981] font-bold">{done}</span> done ·
                    <span className="text-[#002FA7] font-bold ml-1">{scheduled}</span> scheduled
                  </div>
                </td>
                <td className="px-4 py-3 text-xs">{fmtDate(c.start_date)}</td>
                <td className="px-4 py-3 text-xs">{fmtDate(c.end_date)}</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${c.status === "Active" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>{c.status}</span></td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <button
                      onClick={() => onDetails(c)}
                      className="text-[#0a0a0a] hover:underline text-xs uppercase tracking-widest font-bold flex items-center gap-1"
                      data-testid={`amc-details-${c.id}`}
                    >
                      <PanelRightOpen className="w-3 h-3" /> Details
                    </button>
                    <button
                      onClick={() => onEdit(c)}
                      className="text-[#002FA7] hover:underline text-xs uppercase tracking-widest font-bold flex items-center gap-1"
                      data-testid={`amc-edit-${c.id}`}
                    >
                      <Edit2 className="w-3 h-3" /> Edit
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
          {rows.length === 0 && <tr><td colSpan={10} className="text-center text-sm text-[#9CA3AF] py-12">{totalContracts === 0 ? "No AMC contracts yet." : `No contracts match your filters (of ${fullLength} matching).`}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function ContractDetails({ c, onClose }) {
  const visits = c.visits || [];
  const scheduled = visits.filter((v) => v.status === "scheduled").sort((a, b) => (a.scheduled_date || "").localeCompare(b.scheduled_date || ""));
  const completed = visits.filter((v) => v.status === "completed").sort((a, b) => (b.completed_date || b.scheduled_date || "").localeCompare(a.completed_date || a.scheduled_date || ""));
  return (
    <div data-testid="amc-details-content">
      <SheetHeader className="pb-3 border-b border-[#E5E7EB]">
        <SheetTitle className="font-black text-2xl tracking-tight">{c.customer_name}</SheetTitle>
        <div className="text-xs text-[#4B5563] flex flex-wrap gap-2 pt-1">
          <span className="label-eyebrow !text-[10px]">AMC Contract</span>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${c.status === "Active" ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#FEE2E2] text-[#991B1B]"}`}>{c.status}</span>
        </div>
      </SheetHeader>

      <div className="py-4 space-y-5">
        {/* Key facts */}
        <div className="grid grid-cols-2 gap-3">
          <Info label="Phone" value={c.phone || "—"} />
          <Info label="Consumer No." value={c.consumer_number || "—"} />
          <Info label="Place / Project" value={c.place || (c.project_id ? "Linked project" : "—")} />
          <Info label="Plan" value={c.plan || "—"} />
          <Info label="Annual Fee" value={inr(c.annual_fee_inr)} />
          <Info label="Visits / yr" value={`${c.visits_per_year}`} />
          <Info label="Start Date" value={fmtDate(c.start_date)} />
          <Info label="End Date" value={fmtDate(c.end_date)} />
        </div>

        {c.location && (
          <div>
            <div className="label-eyebrow !text-[10px] text-[#4B5563]">Location / Map</div>
            {c.location.startsWith("http")
              ? <a href={c.location} target="_blank" rel="noreferrer" className="text-[#002FA7] hover:underline text-sm break-all">{c.location}</a>
              : <div className="text-sm">{c.location}</div>}
          </div>
        )}
        {c.notes && (
          <div>
            <div className="label-eyebrow !text-[10px] text-[#4B5563]">Notes</div>
            <div className="text-sm whitespace-pre-wrap">{c.notes}</div>
          </div>
        )}

        {/* Visits summary chips */}
        <div className="grid grid-cols-3 gap-2">
          <div className="border border-[#E5E7EB] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold">Total Visits</div>
            <div className="text-2xl font-black mt-1">{visits.length}</div>
          </div>
          <div className="border border-[#E5E7EB] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold">Scheduled</div>
            <div className="text-2xl font-black mt-1 text-[#002FA7]">{scheduled.length}</div>
          </div>
          <div className="border border-[#E5E7EB] p-3 text-center">
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-bold">Completed</div>
            <div className="text-2xl font-black mt-1 text-[#10B981]">{completed.length}</div>
          </div>
        </div>

        {/* Scheduled visits */}
        <div>
          <div className="label-eyebrow !text-[10px] text-[#002FA7] flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" /> Scheduled Visits ({scheduled.length})</div>
          <div className="mt-2 border border-[#E5E7EB] divide-y divide-[#F3F4F6]" data-testid="amc-details-scheduled">
            {scheduled.length === 0 && <div className="p-4 text-xs text-[#9CA3AF] text-center">No scheduled visits.</div>}
            {scheduled.map((v, i) => (
              <div key={v.id} className="px-3 py-2 flex items-start justify-between gap-2" data-testid={`amc-details-visit-${v.id}`}>
                <div>
                  <div className="text-xs font-bold">#{i + 1} · {fmtDate(v.scheduled_date)}</div>
                  {v.technician_name && <div className="text-[10px] text-[#4B5563]">Technician: {v.technician_name}</div>}
                  {v.notes && <div className="text-[10px] text-[#9CA3AF]">{v.notes}</div>}
                </div>
                <span className="text-[10px] uppercase tracking-widest font-bold bg-[#DBEAFE] text-[#1E40AF] px-2 py-0.5">Scheduled</span>
              </div>
            ))}
          </div>
        </div>

        {/* Completed visits */}
        <div>
          <div className="label-eyebrow !text-[10px] text-[#065F46] flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Completed Visits ({completed.length})</div>
          <div className="mt-2 border border-[#E5E7EB] divide-y divide-[#F3F4F6]" data-testid="amc-details-completed">
            {completed.length === 0 && <div className="p-4 text-xs text-[#9CA3AF] text-center">No completed visits yet.</div>}
            {completed.map((v) => (
              <div key={v.id} className="px-3 py-2" data-testid={`amc-details-completed-${v.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-bold">{fmtDate(v.completed_date || v.scheduled_date)}</div>
                  <span className="text-[10px] uppercase tracking-widest font-bold bg-[#D1FAE5] text-[#065F46] px-2 py-0.5">Done</span>
                </div>
                <div className="text-[10px] text-[#4B5563] mt-0.5">
                  Scheduled for {fmtDate(v.scheduled_date)}
                  {v.technician_name && ` · ${v.technician_name}`}
                </div>
                {v.notes && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{v.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={onClose} variant="outline" className="w-full rounded-none uppercase tracking-widest text-xs font-bold h-10" data-testid="amc-details-close">Close</Button>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow !text-[10px] text-[#9CA3AF]">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function VisitsTable({ rows, kind, emptyMsg, onChanged, contractIndex }) {
  const isCompleted = kind === "completed";
  const headers = isCompleted
    ? ["Customer", "Place / Project", "Plan", "Scheduled", "Completed", "Technician", "Notes"]
    : ["Customer", "Place / Project", "Plan", "Scheduled Date", "Technician", "Notes", ""];
  return (
    <div className="border border-[#E5E7EB] overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
          <tr>{headers.map((h) => <th key={h} className="text-left label-eyebrow !text-[10px] px-4 py-3">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((v) => (
            <VisitRow key={`${v.contract_id}-${v.id}`} v={v} isCompleted={isCompleted} onChanged={onChanged} contractIndex={contractIndex} />
          ))}
          {rows.length === 0 && <tr><td colSpan={headers.length} className="text-center text-sm text-[#9CA3AF] py-12">{emptyMsg}</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function VisitRow({ v, isCompleted, onChanged, contractIndex }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [dateVal, setDateVal] = useState(v.scheduled_date || "");
  const [techVal, setTechVal] = useState(v.technician_name || "");
  const [notesVal, setNotesVal] = useState(v.notes || "");

  const c = contractIndex[v.contract_id] || {};
  const placeLabel = v.place || (v.project_id ? "Linked project" : "—");

  const saveReschedule = async () => {
    if (!dateVal) { toast.error("Pick a date"); return; }
    setBusy(true);
    try {
      await api.patch(`/amc-contracts/${v.contract_id}/visits/${v.id}`, {
        scheduled_date: dateVal,
        technician_name: techVal || null,
        notes: notesVal || null,
      });
      toast.success("Visit rescheduled");
      setEditing(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <tr className="border-b border-[#F3F4F6] hover:bg-[#FAFAFA]" data-testid={`amc-visit-row-${v.id}`}>
        <td className="px-4 py-3">
          <div className="font-semibold">{v.customer_name || "—"}</div>
          {v.phone && <div className="text-[10px] text-[#9CA3AF]">{v.phone}</div>}
        </td>
        <td className="px-4 py-3 text-xs text-[#4B5563]">
          {placeLabel}
          {v.location && v.location.startsWith("http") && (
            <a href={v.location} target="_blank" rel="noreferrer" className="ml-2 text-[#002FA7] hover:underline">map ↗</a>
          )}
        </td>
        <td className="px-4 py-3 text-xs">{v.plan || c.plan || "—"}</td>
        <td className="px-4 py-3 text-xs font-bold">{fmtDate(v.scheduled_date)}</td>
        {isCompleted && (
          <td className="px-4 py-3 text-xs text-[#10B981] font-bold">{v.completed_date ? fmtDate(v.completed_date) : "—"}</td>
        )}
        <td className="px-4 py-3 text-xs">{v.technician_name || <span className="text-[#9CA3AF]">Unassigned</span>}</td>
        <td className="px-4 py-3 text-xs text-[#4B5563] max-w-[280px]"><span className="line-clamp-2">{v.notes || "—"}</span></td>
        {!isCompleted && (
          <td className="px-4 py-3 text-right whitespace-nowrap">
            <div className="inline-flex gap-1">
              <Button
                onClick={() => setMarkOpen(true)}
                disabled={busy}
                size="sm"
                className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold bg-[#10B981] hover:bg-[#059669] text-white"
                data-testid={`amc-visit-mark-${v.id}`}
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> Mark Done
              </Button>
              <Button
                onClick={() => setEditing(true)}
                disabled={busy}
                size="sm"
                variant="ghost"
                className="rounded-none h-7 px-2 text-[10px] uppercase tracking-widest font-bold text-[#002FA7]"
                data-testid={`amc-visit-reschedule-${v.id}`}
              >
                <CalendarDays className="w-3 h-3 mr-1" /> Reschedule
              </Button>
            </div>
          </td>
        )}
      </tr>

      {/* Reschedule dialog */}
      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent className="rounded-none border-[#0a0a0a] max-w-md">
          <DialogHeader><DialogTitle className="font-black text-xl tracking-tight">Reschedule Visit</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-xs text-[#4B5563]">
              <b>{v.customer_name}</b> · {v.plan || c.plan || "—"}<br />
              Current scheduled: <b>{fmtDate(v.scheduled_date)}</b>
            </div>
            <div>
              <Label className="label-eyebrow">New Date</Label>
              <Input type="date" min={todayYmd()} value={dateVal} onChange={(e) => setDateVal(e.target.value)} className="rounded-none mt-1 h-10" data-testid="amc-reschedule-date" />
            </div>
            <div>
              <Label className="label-eyebrow">Technician (optional)</Label>
              <Input value={techVal} onChange={(e) => setTechVal(e.target.value)} placeholder="Technician name" className="rounded-none mt-1 h-10" data-testid="amc-reschedule-tech" />
            </div>
            <div>
              <Label className="label-eyebrow">Notes (optional)</Label>
              <textarea value={notesVal} onChange={(e) => setNotesVal(e.target.value)} className="rounded-none mt-1 w-full border border-[#E5E7EB] p-2 text-sm min-h-[60px]" data-testid="amc-reschedule-notes" />
            </div>
            <div className="flex gap-2">
              <Button onClick={saveReschedule} disabled={busy} className="flex-1 rounded-none bg-[#002FA7] hover:bg-[#002480] h-10 uppercase tracking-widest text-xs font-bold" data-testid="amc-reschedule-save">
                {busy ? "Saving…" : "Save Changes"}
              </Button>
              <Button onClick={() => setEditing(false)} variant="outline" className="rounded-none h-10 uppercase tracking-widest text-xs font-bold">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark done dialog */}
      <MarkDoneDialog
        open={markOpen}
        onOpenChange={setMarkOpen}
        v={v}
        onDone={onChanged}
      />
    </>
  );
}

function MarkDoneDialog({ open, onOpenChange, v, onDone }) {
  const today = todayYmd();
  const [completedOn, setCompletedOn] = useState(today);
  const [tech, setTech] = useState(v.technician_name || "");
  const [notes, setNotes] = useState(v.notes || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCompletedOn(today);
      setTech(v.technician_name || "");
      setNotes(v.notes || "");
    }
    // eslint-disable-next-line
  }, [open]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/amc-contracts/${v.contract_id}/visits/${v.id}`, {
        status: "completed",
        completed_date: completedOn,
        technician_name: tech || null,
        notes: notes || null,
      });
      toast.success("Visit marked done");
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none border-[#0a0a0a] max-w-md">
        <DialogHeader><DialogTitle className="font-black text-xl tracking-tight">Mark Visit Done</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <div className="text-xs text-[#4B5563]">
            <b>{v.customer_name}</b> · scheduled <b>{fmtDate(v.scheduled_date)}</b>
          </div>
          <div>
            <Label className="label-eyebrow">Completed On</Label>
            <Input type="date" value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} className="rounded-none mt-1 h-10" data-testid="amc-markdone-date" />
          </div>
          <div>
            <Label className="label-eyebrow">Technician</Label>
            <Input value={tech} onChange={(e) => setTech(e.target.value)} className="rounded-none mt-1 h-10" data-testid="amc-markdone-tech" />
          </div>
          <div>
            <Label className="label-eyebrow">Service Notes</Label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was serviced, findings, next actions…" className="rounded-none mt-1 w-full border border-[#E5E7EB] p-2 text-sm min-h-[80px]" data-testid="amc-markdone-notes" />
          </div>
          <Button onClick={save} disabled={busy} className="w-full rounded-none bg-[#10B981] hover:bg-[#059669] h-10 uppercase tracking-widest text-xs font-bold" data-testid="amc-markdone-save">
            {busy ? "Saving…" : "Confirm Done"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function addYears(iso, years) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setFullYear(d.getFullYear() + Number(years));
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function inferYearsFromRange(start, end) {
  if (!start || !end) return 5;
  for (const y of [1, 2, 3, 5, 7, 10, 15, 20]) {
    if (addYears(start, y) === end) return y;
  }
  return "custom";
}

function AMCDialog({ onDone, existing }) {
  const isEdit = Boolean(existing);
  const [projects, setProjects] = useState([]);
  const today = todayYmd();
  const initialYears = existing ? inferYearsFromRange(existing.start_date, existing.end_date) : 5;
  const initialStart = existing?.start_date || today;
  const initialEnd = existing?.end_date || (initialYears === "custom" ? today : addYears(initialStart, initialYears));
  const [mode, setMode] = useState(existing ? (existing.project_id ? "project" : "direct") : "project");
  const [durationYears, setDurationYears] = useState(initialYears);
  const [f, setF] = useState({
    project_id: existing?.project_id || "",
    customer_name: existing?.customer_name || "",
    phone: existing?.phone || "",
    place: existing?.place || "",
    location: existing?.location || "",
    consumer_number: existing?.consumer_number || "",
    start_date: initialStart,
    end_date: initialEnd,
    plan: existing?.plan || "Standard",
    annual_fee_inr: existing?.annual_fee_inr ?? 12000,
    visits_per_year: existing?.visits_per_year ?? 2,
    status: existing?.status || "Active",
    notes: existing?.notes || "",
  });
  useEffect(() => { api.get("/projects/options").then((r) => setProjects(r.data)); }, []);
  const set = (k) => (v) => setF({ ...f, [k]: v.target ? v.target.value : v });

  const setStartDate = (e) => {
    const newStart = e.target.value;
    if (durationYears === "custom") {
      setF({ ...f, start_date: newStart });
    } else {
      setF({ ...f, start_date: newStart, end_date: addYears(newStart, durationYears) });
    }
  };
  const setDuration = (v) => {
    setDurationYears(v === "custom" ? "custom" : Number(v));
    if (v !== "custom") {
      setF({ ...f, end_date: addYears(f.start_date, Number(v)) });
    }
  };

  const preview = useMemo(() => {
    const s = new Date(f.start_date);
    const e = new Date(f.end_date);
    if (isNaN(s) || isNaN(e) || e <= s) return [];
    const days = (e - s) / 86400000;
    const total = Math.max(1, Math.round((f.visits_per_year || 2) * days / 365.25));
    const out = [];
    for (let i = 0; i < total; i++) {
      const offset = Math.round((i + 0.5) * days / total);
      const d = new Date(s); d.setDate(d.getDate() + offset);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [f.start_date, f.end_date, f.visits_per_year]);

  const onProjectPick = (id) => {
    const p = projects.find((x) => x.id === id);
    setF({ ...f, project_id: id, customer_name: p?.customer_name || f.customer_name });
  };

  const submit = async () => {
    if (mode === "project" && !f.project_id) { toast.error("Pick a project (or switch to Direct Entry)"); return; }
    if (!f.customer_name?.trim()) { toast.error("Customer name is required"); return; }
    const payload = {
      ...f,
      project_id: mode === "direct" ? null : (f.project_id || null),
      place: mode === "direct" ? (f.place || null) : null,
      consumer_number: mode === "direct" ? (f.consumer_number || null) : null,
      location: f.location || null,
      notes: f.notes || null,
      annual_fee_inr: parseFloat(f.annual_fee_inr || 0),
      visits_per_year: parseInt(f.visits_per_year || 1),
    };
    try {
      if (isEdit) {
        await api.patch(`/amc-contracts/${existing.id}`, payload);
        toast.success("AMC updated");
      } else {
        await api.post("/amc-contracts", payload);
        toast.success(`AMC saved — ${preview.length} visit${preview.length === 1 ? "" : "s"} auto-scheduled`);
      }
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-xl max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">{isEdit ? "Edit AMC Contract" : "New AMC Contract"}</DialogTitle></DialogHeader>
      <Tabs value={mode} onValueChange={setMode} className="pt-2">
        <TabsList className="rounded-none grid grid-cols-2 w-full bg-[#F3F4F6]">
          <TabsTrigger value="project" className="rounded-none uppercase tracking-widest text-[10px] font-bold" data-testid="amc-mode-project">From Existing Project</TabsTrigger>
          <TabsTrigger value="direct" className="rounded-none uppercase tracking-widest text-[10px] font-bold" data-testid="amc-mode-direct">Direct Entry (No Project)</TabsTrigger>
        </TabsList>

        <TabsContent value="project" className="space-y-3 pt-4">
          <div>
            <Label className="label-eyebrow">Project</Label>
            <SearchablePicker
              items={projects}
              value={f.project_id}
              onChange={onProjectPick}
              matchItem={projectMatch}
              renderRow={renderProjectRow}
              selectedLabel={(p) => `${p.customer_name} — ${p.qr_code}`}
              placeholder="Search by customer, QR or location…"
              testid="amc-project-select"
              emptyHint="No projects match your search."
            />
          </div>
          <Field label="Customer Name" value={f.customer_name} onChange={set("customer_name")} testid="amc-customer" />
          <Field label="Phone" value={f.phone} onChange={set("phone")} testid="amc-phone" />
        </TabsContent>

        <TabsContent value="direct" className="space-y-3 pt-4">
          <Field label="Customer Name *" value={f.customer_name} onChange={set("customer_name")} testid="amc-direct-customer" />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Place" value={f.place} onChange={set("place")} testid="amc-place" placeholder="Edappally, Kochi" />
            <Field label="Phone" value={f.phone} onChange={set("phone")} testid="amc-direct-phone" />
          </div>
          <Field label="Consumer Number (KSEB)" value={f.consumer_number} onChange={set("consumer_number")} testid="amc-consumer-number" placeholder="e.g. 1188-0001" />
        </TabsContent>

        <div className="space-y-3 pt-4 border-t border-[#E5E7EB] mt-4">
          <Field
            label="Location / Address / Map Link"
            value={f.location}
            onChange={set("location")}
            testid="amc-location"
            placeholder="Full address or https://maps.google.com/…"
          />
          <div className="grid grid-cols-3 gap-2">
            <Field label="Start Date" type="date" value={f.start_date} onChange={setStartDate} testid="amc-start" />
            <div>
              <Label className="label-eyebrow">Duration</Label>
              <Select value={String(durationYears)} onValueChange={setDuration}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="amc-duration">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 5, 7, 10, 15, 20].map((y) => (
                    <SelectItem key={y} value={String(y)}>{y} year{y === 1 ? "" : "s"}</SelectItem>
                  ))}
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field
              label={durationYears === "custom" ? "End Date" : "End Date (auto)"}
              type="date"
              value={f.end_date}
              onChange={set("end_date")}
              readOnly={durationYears !== "custom"}
              testid="amc-end"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="label-eyebrow">Plan</Label>
              <Select value={f.plan} onValueChange={set("plan")}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="amc-plan"><SelectValue /></SelectTrigger>
                <SelectContent>{["Basic", "Standard", "Premium"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Field label="Fee/yr (₹)" type="number" value={f.annual_fee_inr} onChange={set("annual_fee_inr")} testid="amc-fee" />
            <Field label="Visits/yr" type="number" value={f.visits_per_year} onChange={set("visits_per_year")} testid="amc-visits-per-year" />
          </div>
          {isEdit && (
            <div>
              <Label className="label-eyebrow">Status</Label>
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger className="rounded-none mt-2 h-10" data-testid="amc-status"><SelectValue /></SelectTrigger>
                <SelectContent>{["Active", "Paused", "Expired", "Cancelled"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="label-eyebrow">Notes</Label>
            <textarea
              value={f.notes}
              onChange={(e) => setF({ ...f, notes: e.target.value })}
              placeholder="Special requests, gate code, technician preferences…"
              className="rounded-none mt-2 w-full border border-[#E5E7EB] p-2 text-sm min-h-[60px]"
              data-testid="amc-notes"
            />
          </div>

          <div className="border border-[#002FA7] bg-[#EFF6FF] p-3" data-testid="amc-visit-preview">
            <div className="flex items-center gap-2 label-eyebrow !text-[10px] text-[#002FA7]">
              <CalendarDays className="w-3.5 h-3.5" /> Auto-scheduled Visits ({preview.length})
            </div>
            <div className="text-[11px] text-[#1F2937] mt-2 leading-relaxed">
              {preview.length === 0
                ? <span className="text-[#DC2626]">Pick a valid date range to see visit forecast.</span>
                : preview.map((d, i) => (
                    <span key={d} className="inline-block bg-white border border-[#E5E7EB] px-2 py-0.5 mr-1 mb-1 font-mono">
                      #{i + 1} · {d}
                    </span>
                  ))}
            </div>
            <div className="text-[10px] text-[#4B5563] mt-1">
              {isEdit
                ? "On edit, only auto-generated visits are regenerated — completed visits are preserved."
                : "Calculated from the contract period and visits/yr. Spaced evenly (half-cycle gaps)."}
            </div>
          </div>

          <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="amc-submit">
            {isEdit ? "Update AMC" : "Save AMC"}
          </Button>
        </div>
      </Tabs>
    </DialogContent>
  );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, readOnly }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`rounded-none mt-2 h-10 ${readOnly ? "bg-[#F3F4F6] text-[#6B7280] cursor-not-allowed" : ""}`}
        data-testid={testid}
      />
    </div>
  );
}
